import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { BookmarkIcon, CheckIcon } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '../components/common/PageHeader';
const timeline = [{
  month: '2025.06',
  phase: '语言备考',
  highlight: false,
  desc: '雅思/托福首考'
}, {
  month: '2025.09',
  phase: '标化考试',
  highlight: true,
  desc: 'GRE / GMAT'
}, {
  month: '2025.10',
  phase: '文书准备',
  highlight: true,
  desc: 'PS / CV / 推荐信'
}, {
  month: '2025.11',
  phase: '网申开始',
  highlight: true,
  desc: '提交网申材料'
}, {
  month: '2026.01',
  phase: '面试季',
  highlight: false,
  desc: '院校面试'
}, {
  month: '2026.03',
  phase: 'Offer 收割',
  highlight: false,
  desc: '确认院校'
}, {
  month: '2026.07',
  phase: '签证 & 行前',
  highlight: false,
  desc: '签证 + 入学准备'
}];
const bgTabs = ['实习', '科研', '竞赛', '语言备考'] as const;
const bgTips: Record<string, {
  title: string;
  tags: string[];
}[]> = {
  实习: [{
    title: '至少一段大厂或行业头部实习，时长 ≥ 2 个月',
    tags: ['大厂', '商科必备']
  }, {
    title: '关注岗位与申请专业的相关性',
    tags: ['相关性']
  }],
  科研: [{
    title: '校内导师跟课题或参加海外科研项目',
    tags: ['理工', '研究型']
  }, {
    title: '尝试发表 1-2 篇论文或会议成果',
    tags: ['加分项']
  }],
  竞赛: [{
    title: '参加专业相关国际/国内竞赛获奖',
    tags: ['含金量']
  }, {
    title: '商科可参加 CFA / Kaggle 等',
    tags: ['商科', '数据']
  }],
  语言备考: [{
    title: '雅思 7.0 / 托福 100+ 起步',
    tags: ['名校门槛']
  }, {
    title: '尽早一战，预留二战时间',
    tags: ['节奏']
  }]
};
const checklistInit = ['个人陈述 PS', '简历 CV', '推荐信 ×2-3', '成绩单（中英文）', '语言成绩单', '标化成绩单', '护照', '资金证明', '在读/毕业证明'];
export function Planner() {
  const [tab, setTab] = useState<(typeof bgTabs)[number]>('实习');
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [savedTips, setSavedTips] = useState<Set<string>>(new Set());
  const toggle = (k: string) => {
    const ns = new Set(checked);
    ns.has(k) ? ns.delete(k) : ns.add(k);
    setChecked(ns);
  };
  const saveTip = (k: string) => {
    const ns = new Set(savedTips);
    ns.has(k) ? ns.delete(k) : ns.add(k);
    setSavedTips(ns);
    toast(ns.has(k) ? '已收藏建议' : '已取消收藏');
  };
  return <div className="bg-slate-50 min-h-screen pb-20" data-id="element-586">
      <PageHeader title="申请规划助手" data-id="element-587" />

      {/* 时间线 */}
      <div className="m-3 bg-white rounded-2xl p-4" data-id="element-588">
        <div className="font-bold text-slate-800 mb-3 flex items-center gap-2" data-id="element-589">
          <span className="w-1 h-4 bg-blue-500 rounded" data-id="element-590" /> 申请时间线
        </div>
        <div className="relative pl-6" data-id="element-591">
          <div className="absolute left-2 top-2 bottom-2 w-0.5 bg-blue-100" data-id="element-592" />
          {timeline.map(t => <div key={t.month} className="relative pb-4 last:pb-0" data-id="element-593">
              <div className={`absolute -left-[18px] top-1 w-3 h-3 rounded-full border-2 ${t.highlight ? 'bg-rose-500 border-rose-200' : 'bg-blue-500 border-blue-100'}`} data-id="element-594" />
              <div className="text-xs text-slate-500" data-id="element-595">{t.month}</div>
              <div className={`text-sm font-bold ${t.highlight ? 'text-rose-600' : 'text-slate-800'}`} data-id="element-596">
                {t.phase}
              </div>
              <div className="text-xs text-slate-500 mt-0.5" data-id="element-597">{t.desc}</div>
            </div>)}
        </div>
      </div>

      {/* 背景提升建议 */}
      <div className="m-3 bg-white rounded-2xl p-4" data-id="element-598">
        <div className="font-bold text-slate-800 mb-3 flex items-center gap-2" data-id="element-599">
          <span className="w-1 h-4 bg-blue-500 rounded" data-id="element-600" /> 背景提升建议
        </div>
        <div className="flex gap-2 mb-3 border-b border-slate-100" data-id="element-601">
          {bgTabs.map(t => <button key={t} onClick={() => setTab(t)} className={`pb-2 text-sm relative ${tab === t ? 'text-blue-600 font-bold' : 'text-slate-500'}`} data-id="element-602">
              {t}
              {tab === t && <motion.div layoutId="bg-tab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded" data-id="element-603" />}
            </button>)}
        </div>
        <div className="space-y-2" data-id="element-604">
          {bgTips[tab].map(tip => <div key={tip.title} className="bg-slate-50 rounded-xl p-3 flex items-start gap-2" data-id="element-605">
              <div className="flex-1" data-id="element-606">
                <div className="text-sm text-slate-800" data-id="element-607">{tip.title}</div>
                <div className="flex flex-wrap gap-1 mt-1.5" data-id="element-608">
                  {tip.tags.map(tg => <span key={tg} className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded" data-id="element-609">
                      {tg}
                    </span>)}
                </div>
              </div>
              <button onClick={() => saveTip(tip.title)} aria-label="收藏建议" data-id="element-610">
                <BookmarkIcon size={16} className={savedTips.has(tip.title) ? 'text-blue-600' : 'text-slate-300'} fill={savedTips.has(tip.title) ? '#2b6ef5' : 'none'} data-id="element-611" />
              </button>
            </div>)}
        </div>
      </div>

      {/* 材料清单 */}
      <div className="m-3 bg-white rounded-2xl p-4" data-id="element-612">
        <div className="font-bold text-slate-800 mb-3 flex items-center gap-2" data-id="element-613">
          <span className="w-1 h-4 bg-blue-500 rounded" data-id="element-614" /> 材料清单
          <span className="ml-auto text-xs text-slate-400 font-normal" data-id="element-615">
            {checked.size}/{checklistInit.length}
          </span>
        </div>
        <div className="space-y-2" data-id="element-616">
          {checklistInit.map(c => {
          const ok = checked.has(c);
          return <button key={c} onClick={() => toggle(c)} className="w-full flex items-center gap-3 py-2 text-left" data-id="element-617">
                <span className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${ok ? 'bg-blue-600 border-blue-600' : 'border-slate-300'}`} data-id="element-618">
                  {ok && <CheckIcon size={12} className="text-white" strokeWidth={3} data-id="element-619" />}
                </span>
                <span className={`text-sm ${ok ? 'text-slate-400 line-through' : 'text-slate-700'}`} data-id="element-620">
                  {c}
                </span>
              </button>;
        })}
        </div>
      </div>
    </div>;
}